import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_ENDPOINTS } from './ApiConfig';

// Create the session context
const SessionContext = createContext();

// Custom hook to use the session context
export const useSession = () => useContext(SessionContext);

// Session provider component
export const SessionProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const storedSession = localStorage.getItem('isAuthenticated');
    return storedSession ? JSON.parse(storedSession) : false;
  });
  
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  // Update localStorage when authentication state changes
  useEffect(() => {
    localStorage.setItem('isAuthenticated', JSON.stringify(isAuthenticated));
    localStorage.setItem('user', JSON.stringify(user));
  }, [isAuthenticated, user]);

  // Login function - makes API request to validate credentials
  const login = async (email, password) => {
    try {
      // Make POST request to the login endpoint
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });
      
      // Parse the JSON response
      const responseData = await response.json();
      
      // The response is an array with a single user object
      if (Array.isArray(responseData) && responseData.length > 0) {
        const userData = responseData[0];
        
        // Check if login was successful by verifying if an id is returned
        if (userData.id) {
          setIsAuthenticated(true);
          
          // Store the user information from the response, including auth data
          setUser({ 
            email: userData.email,
            role: userData.role || 'admin',
            id: userData.id, // Store the user id
            // Store auth data for further requests
            access_token: userData.access_token,
            refresh_token: userData.refresh_token,
            expires_in: userData.expires_in,
            expires_at: userData.expires_at
          });
          
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
  };

  // Provide the session context to children components
  return (
    <SessionContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </SessionContext.Provider>
  );
};
